import { http } from "@/lib/http";
import { rewriteMediaUrl } from "@/lib/media-url";
import type { AdminSettings, SettingsUpdateInput } from "@/types/settings";

function normalizeMediaUrlList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => rewriteMediaUrl(typeof item === "string" ? item : null))
    .filter((item): item is string => Boolean(item));
}

export function normalizeAdminSettings(item: Record<string, unknown>): AdminSettings {
  const rawVisibility = item.is_publicly_visible;
  const rawHasForum = item.has_forum;
  const rawShowCreatorRating = item.show_creator_rating;
  const rawTestimonialDiscountEnabled = item.testimonial_discount_enabled;
  const rawCustomLandingEnabled = item.custom_landing_enabled;

  return {
    siteSlug: item.site_slug as string | null | undefined,
    siteName: item.site_name as string | null | undefined,
    siteCategory: item.site_category as string | null | undefined,
    homeHeadline: item.home_headline as string | null | undefined,
    homeSubheadline: item.home_subheadline as string | null | undefined,
    salesPitchText1: item.sales_pitch_1 as string | null | undefined,
    salesPitchText2: item.sales_pitch_2 as string | null | undefined,
    salesPitchText3: item.sales_pitch_3 as string | null | undefined,
    titleFolders: item.title_folders as string | null | undefined,
    titleFeed: item.title_feed as string | null | undefined,
    siteLogo: rewriteMediaUrl(item.site_logo as string | null | undefined),
    siteBanner: rewriteMediaUrl(item.site_banner as string | null | undefined),
    promoVideo: rewriteMediaUrl(item.promo_video as string | null | undefined),
    socialProofVideos: normalizeMediaUrlList(item.social_proof_videos),
    colorTheme: item.color_theme as string | null | undefined,
    accentColor: item.accent_color as string | null | undefined,
    accentSoftColor: item.accent_soft_color as string | null | undefined,
    secondaryColor: item.secondary_color as string | null | undefined,
    surfaceColor: item.surface_color as string | null | undefined,
    backgroundFromColor: item.background_from_color as string | null | undefined,
    backgroundToColor: item.background_to_color as string | null | undefined,
    siteLogoPosition: item.site_logo_position as string | null | undefined,
    isPubliclyVisible:
      typeof rawVisibility === "boolean"
        ? rawVisibility
        : rawVisibility == null
          ? null
          : String(rawVisibility) === "1" || String(rawVisibility).toLowerCase() === "true",
    hasForum:
      typeof rawHasForum === "boolean"
        ? rawHasForum
        : rawHasForum == null
          ? null
          : String(rawHasForum) === "1" || String(rawHasForum).toLowerCase() === "true",
    showCreatorRating:
      typeof rawShowCreatorRating === "boolean"
        ? rawShowCreatorRating
        : rawShowCreatorRating == null
          ? null
          : String(rawShowCreatorRating) === "1" || String(rawShowCreatorRating).toLowerCase() === "true",
    testimonialDiscountEnabled:
      typeof rawTestimonialDiscountEnabled === "boolean"
        ? rawTestimonialDiscountEnabled
        : rawTestimonialDiscountEnabled == null
          ? null
          : String(rawTestimonialDiscountEnabled) === "1" || String(rawTestimonialDiscountEnabled).toLowerCase() === "true",
    testimonialDiscountPercent:
      item.testimonial_discount_percent == null
        ? null
        : Number(item.testimonial_discount_percent),
    customLandingEnabled:
      typeof rawCustomLandingEnabled === "boolean"
        ? rawCustomLandingEnabled
        : rawCustomLandingEnabled == null
          ? null
          : String(rawCustomLandingEnabled) === "1" || String(rawCustomLandingEnabled).toLowerCase() === "true",
    customLandingHtml: item.custom_landing_html as string | null | undefined,
    landingImage1Label: item.landing_image_1_label as string | null | undefined,
    landingImage1: rewriteMediaUrl(item.landing_image_1 as string | null | undefined),
    landingImage2Label: item.landing_image_2_label as string | null | undefined,
    landingImage2: rewriteMediaUrl(item.landing_image_2 as string | null | undefined),
    landingImage3Label: item.landing_image_3_label as string | null | undefined,
    landingImage3: rewriteMediaUrl(item.landing_image_3 as string | null | undefined),
    landingMembersCountOverride: item.landing_members_count_override as string | null | undefined,
    landingLikesCountOverride: item.landing_likes_count_override as string | null | undefined,
    landingCommentsCountOverride: item.landing_comments_count_override as string | null | undefined,
    landingAverageRatingOverride: item.landing_average_rating_override as string | null | undefined,
    landingReviewsCountOverride: item.landing_reviews_count_override as string | null | undefined,
    landingSocialProofText: item.landing_social_proof_text as string | null | undefined,
    membersCount: item.members_count == null ? null : String(item.members_count),
    likesCount: item.likes_count == null ? null : String(item.likes_count),
    commentsCount: item.comments_count == null ? null : String(item.comments_count),
    averageRating: item.average_rating == null ? null : String(item.average_rating),
    reviewsCount: item.reviews_count == null ? null : String(item.reviews_count),
    socialProofText: item.social_proof_text as string | null | undefined,
  };
}

export async function fetchAdminSettings() {
  const response = await http.get("/api/v1/admin/settings");
  return normalizeAdminSettings((response.data?.data ?? {}) as Record<string, unknown>);
}

export async function fetchSiteSettings() {
  const response = await http.get("/api/v1/site-info");
  return normalizeAdminSettings((response.data?.data ?? {}) as Record<string, unknown>);
}

export async function updateAdminSettings(input: SettingsUpdateInput) {
  const formData = new FormData();
  formData.set("site_name", input.siteName);
  formData.set("site_category", input.siteCategory ?? "Music");
  formData.set("home_headline", input.homeHeadline ?? "");
  formData.set("home_subheadline", input.homeSubheadline ?? "");
  formData.set("sales_pitch_1", input.salesPitch1 ?? "");
  formData.set("sales_pitch_2", input.salesPitch2 ?? "");
  formData.set("sales_pitch_3", input.salesPitch3 ?? "");
  formData.set("title_folders", input.titleFolders ?? "");
  formData.set("title_feed", input.titleFeed ?? "");
  formData.set("color_theme", input.colorTheme ?? "dark");
  formData.set("accent_color", input.accentColor ?? "#7885ff");
  formData.set("accent_soft_color", input.accentSoftColor ?? "#9aa2ff");
  formData.set("secondary_color", input.secondaryColor ?? "#9b8cff");
  formData.set("surface_color", input.surfaceColor ?? "#181c33");
  formData.set("background_from_color", input.backgroundFromColor ?? "#0a0c16");
  formData.set("background_to_color", input.backgroundToColor ?? "#06070d");
  formData.set("site_logo_position", input.siteLogoPosition ?? "");
  formData.set("is_publicly_visible", input.isPubliclyVisible ? "1" : "0");
  formData.set("has_forum", input.hasForum ? "1" : "0");
  formData.set("show_creator_rating", input.showCreatorRating ? "1" : "0");
  formData.set("testimonial_discount_enabled", input.testimonialDiscountEnabled ? "1" : "0");
  formData.set("testimonial_discount_percent", String(input.testimonialDiscountPercent ?? 0));
  formData.set("custom_landing_enabled", input.customLandingEnabled ? "1" : "0");
  formData.set("custom_landing_html", input.customLandingHtml ?? "");
  formData.set("landing_image_1_label", input.landingImage1Label ?? "hero-shot");
  formData.set("landing_image_2_label", input.landingImage2Label ?? "product-shot");
  formData.set("landing_image_3_label", input.landingImage3Label ?? "social-proof");
  formData.set("landing_members_count_override", input.landingMembersCountOverride ?? "");
  formData.set("landing_likes_count_override", input.landingLikesCountOverride ?? "");
  formData.set("landing_comments_count_override", input.landingCommentsCountOverride ?? "");
  formData.set("landing_average_rating_override", input.landingAverageRatingOverride ?? "");
  formData.set("landing_reviews_count_override", input.landingReviewsCountOverride ?? "");
  formData.set("landing_social_proof_text", input.landingSocialProofText ?? "");

  if (input.logo) {
    formData.set("site_logo", input.logo);
  }
  if (input.banner) {
    formData.set("site_banner", input.banner);
  }
  if (input.promoVideo) {
    formData.set("promo_video", input.promoVideo);
  }
  input.socialProofVideos?.forEach((file) => {
    formData.append("social_proof_videos[]", file);
  });
  if (input.landingImage1) {
    formData.set("landing_image_1", input.landingImage1);
  }
  if (input.landingImage2) {
    formData.set("landing_image_2", input.landingImage2);
  }
  if (input.landingImage3) {
    formData.set("landing_image_3", input.landingImage3);
  }

  await http.post("/api/v1/admin/settings", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function generateLandingPageHtml(input: {
  siteName: string;
  siteCategory?: string;
  homeHeadline?: string;
  homeSubheadline?: string;
  salesPitch1?: string;
  salesPitch2?: string;
  salesPitch3?: string;
  titleFolders?: string;
  titleFeed?: string;
  colorTheme?: string;
  accentColor?: string;
  accentSoftColor?: string;
  secondaryColor?: string;
  surfaceColor?: string;
  backgroundFromColor?: string;
  backgroundToColor?: string;
  landingImage1Label?: string;
  landingImage1?: string;
  landingImage2Label?: string;
  landingImage2?: string;
  landingImage3Label?: string;
  landingImage3?: string;
  existingHtml?: string;
  brief?: string;
}) {
  const response = await http.post("/api/v1/admin/settings/generate-landing", {
    site_name: input.siteName,
    site_category: input.siteCategory ?? "",
    home_headline: input.homeHeadline ?? "",
    home_subheadline: input.homeSubheadline ?? "",
    sales_pitch_1: input.salesPitch1 ?? "",
    sales_pitch_2: input.salesPitch2 ?? "",
    sales_pitch_3: input.salesPitch3 ?? "",
    title_folders: input.titleFolders ?? "",
    title_feed: input.titleFeed ?? "",
    color_theme: input.colorTheme ?? "dark",
    accent_color: input.accentColor ?? "#7885ff",
    accent_soft_color: input.accentSoftColor ?? "#9aa2ff",
    secondary_color: input.secondaryColor ?? "#9b8cff",
    surface_color: input.surfaceColor ?? "#181c33",
    background_from_color: input.backgroundFromColor ?? "#0a0c16",
    background_to_color: input.backgroundToColor ?? "#06070d",
    landing_image_1_label: input.landingImage1Label ?? "",
    landing_image_1_url: input.landingImage1 ?? "",
    landing_image_2_label: input.landingImage2Label ?? "",
    landing_image_2_url: input.landingImage2 ?? "",
    landing_image_3_label: input.landingImage3Label ?? "",
    landing_image_3_url: input.landingImage3 ?? "",
    existing_html: input.existingHtml ?? "",
    brief: input.brief ?? "",
  });

  return String(response.data?.data?.html ?? "");
}

export async function streamLandingPageHtml(
  input: {
    siteName: string;
    siteCategory?: string;
    homeHeadline?: string;
    homeSubheadline?: string;
    salesPitch1?: string;
    salesPitch2?: string;
    salesPitch3?: string;
    titleFolders?: string;
    titleFeed?: string;
    colorTheme?: string;
    accentColor?: string;
    accentSoftColor?: string;
    secondaryColor?: string;
    surfaceColor?: string;
    backgroundFromColor?: string;
    backgroundToColor?: string;
    landingImage1Label?: string;
    landingImage1?: string;
    landingImage2Label?: string;
    landingImage2?: string;
    landingImage3Label?: string;
    landingImage3?: string;
    existingHtml?: string;
    brief?: string;
  },
  options: {
    baseUrl: string;
    token?: string;
    onChunk: (chunk: string, nextHtml: string) => void;
  },
) {
  const response = await fetch(`${options.baseUrl}/api/v1/admin/settings/generate-landing-stream`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
    },
    body: JSON.stringify({
      site_name: input.siteName,
      site_category: input.siteCategory ?? "",
      home_headline: input.homeHeadline ?? "",
      home_subheadline: input.homeSubheadline ?? "",
      sales_pitch_1: input.salesPitch1 ?? "",
      sales_pitch_2: input.salesPitch2 ?? "",
    sales_pitch_3: input.salesPitch3 ?? "",
    title_folders: input.titleFolders ?? "",
    title_feed: input.titleFeed ?? "",
    color_theme: input.colorTheme ?? "dark",
    accent_color: input.accentColor ?? "#7885ff",
    accent_soft_color: input.accentSoftColor ?? "#9aa2ff",
    secondary_color: input.secondaryColor ?? "#9b8cff",
    surface_color: input.surfaceColor ?? "#181c33",
    background_from_color: input.backgroundFromColor ?? "#0a0c16",
    background_to_color: input.backgroundToColor ?? "#06070d",
    landing_image_1_label: input.landingImage1Label ?? "",
    landing_image_1_url: input.landingImage1 ?? "",
    landing_image_2_label: input.landingImage2Label ?? "",
      landing_image_2_url: input.landingImage2 ?? "",
      landing_image_3_label: input.landingImage3Label ?? "",
      landing_image_3_url: input.landingImage3 ?? "",
      existing_html: input.existingHtml ?? "",
      brief: input.brief ?? "",
    }),
  });

  if (!response.ok || !response.body) {
    throw new Error("Failed to stream landing page.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulatedHtml = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";

    for (const event of events) {
      const dataLine = event
        .split("\n")
        .map((line) => line.trim())
        .find((line) => line.startsWith("data:"));

      if (!dataLine) {
        continue;
      }

      const json = dataLine.slice(5).trim();
      if (!json) {
        continue;
      }

      const payload = JSON.parse(json) as { type?: string; chunk?: string; html?: string };
      if (payload.type === "chunk" && payload.chunk) {
        accumulatedHtml += payload.chunk;
        options.onChunk(payload.chunk, accumulatedHtml);
      }

      if (payload.type === "done") {
        return String(payload.html ?? accumulatedHtml);
      }
    }
  }

  return accumulatedHtml;
}

export async function generateColorScheme(input: {
  siteName: string;
  siteCategory?: string;
  colorTheme?: string;
  prompt: string;
}) {
  const response = await http.post("/api/v1/admin/settings/generate-color-scheme", {
    site_name: input.siteName,
    site_category: input.siteCategory ?? "",
    color_theme: input.colorTheme ?? "dark",
    prompt: input.prompt,
  });

  const payload = (response.data?.data ?? {}) as Record<string, unknown>;

  return {
    colorTheme: String(payload.color_theme ?? "dark"),
    accentColor: String(payload.accent_color ?? "#7885ff"),
    accentSoftColor: String(payload.accent_soft_color ?? "#9aa2ff"),
    secondaryColor: String(payload.secondary_color ?? "#9b8cff"),
    surfaceColor: String(payload.surface_color ?? "#181c33"),
    backgroundFromColor: String(payload.background_from_color ?? "#0a0c16"),
    backgroundToColor: String(payload.background_to_color ?? "#06070d"),
  };
}

export async function generateSiteCopy(input: {
  siteName?: string;
  siteCategory?: string;
  homeHeadline?: string;
  homeSubheadline?: string;
  salesPitch1?: string;
  salesPitch2?: string;
  salesPitch3?: string;
  titleFeed?: string;
  titleFolders?: string;
  colorTheme?: string;
  prompt: string;
}) {
  const response = await http.post("/api/v1/admin/settings/generate-copy", {
    site_name: input.siteName ?? "",
    site_category: input.siteCategory ?? "",
    home_headline: input.homeHeadline ?? "",
    home_subheadline: input.homeSubheadline ?? "",
    sales_pitch_1: input.salesPitch1 ?? "",
    sales_pitch_2: input.salesPitch2 ?? "",
    sales_pitch_3: input.salesPitch3 ?? "",
    title_feed: input.titleFeed ?? "",
    title_folders: input.titleFolders ?? "",
    color_theme: input.colorTheme ?? "dark",
    prompt: input.prompt,
  });

  const payload = (response.data?.data ?? {}) as Record<string, unknown>;

  return {
    homeHeadline: String(payload.home_headline ?? ""),
    homeSubheadline: String(payload.home_subheadline ?? ""),
    salesPitch1: String(payload.sales_pitch_1 ?? ""),
    salesPitch2: String(payload.sales_pitch_2 ?? ""),
    salesPitch3: String(payload.sales_pitch_3 ?? ""),
    titleFeed: String(payload.title_feed ?? ""),
    titleFolders: String(payload.title_folders ?? ""),
  };
}

export async function deleteSettingsAsset(
  type: "site_logo" | "site_banner" | "promo_video" | "social_proof_videos" | "site_icon" | "posts_background" | "landing_image_1" | "landing_image_2" | "landing_image_3",
) {
  await http.delete(`/api/v1/admin/settings/${type}`);
}

export async function deleteSettingsFile(type: "social_proof_videos", index: number) {
  await http.delete(`/api/v1/admin/settings/${type}/files/${index}`);
}
